import React, { useState } from 'react'
import Modal from '../common/Modal'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { handlePatchRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { formatTime } from '../../utils/date.util'
import { useMutation } from '@tanstack/react-query'
import DisplayActiveSuspended from './DisplayActiveSuspended'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import USER_TYPE from '../../constants/userType'

const content = {
  retailer: {
    modalTitle: 'Suspend This Retailer?',
    modalDescription:
      'Are you sure you want to suspend this retailer? Their access to the platform will be restricted until further notice. This action can be reversed later.',
  },
  customer: {
    modalTitle: 'Suspend This Customer?',
    modalDescription:
      'Are you sure you want to suspend this customer? Their access to the platform will be restricted until further notice. This action can be reversed later.',
  },
  wholeseller: {
    modalTitle: 'Suspend This Wholeseller?',
    modalDescription:
      'Are you sure you want to suspend this wholeseller? Their access to the platform will be restricted until further notice. This action can be reversed later.',
  },
}

const SuspendModal = ({ isOpen, onClose, user, shopSlug }) => {
  const [suspensionReason, setSuspensionReason] = useState('')
  const { id, name, buildingName, shopNumber, floor, isActive, createdAt, userType } = user || {}

  const suspendMutation = useMutation({
    mutationFn: () =>
      handlePatchRequest(`/admin/${userType}s/${id}/suspend`, {
        suspensionReason,
      }),
    onSuccess: () => {
      if (userType === USER_TYPE.retailer) {
        QueryKeys.adminRetailerDetails.invalidate(shopSlug)
        QueryKeys.adminListRetailers.invalidateAll()
      } else {
        QueryKeys.adminWholesellerDetails.invalidate(shopSlug)
        QueryKeys.adminListWholesellers.invalidateAll()
      }

      showToaster('success', `${name} has been suspended successfully`)
      onClose()
    },
    onError: () => {
      showToaster('error', `${name} not suspended!`)
    },
  })

  const isConfirming = suspendMutation.isLoading
  const handleConfirm = async () => {
    if (!suspensionReason.trim()) {
      showToaster('error', 'Please provide a reason for suspension.', 3000)
      return
    }
    suspendMutation.mutateAsync()
  }

  const renderDisplayItem = (label, value) => {
    return (
      <div className="flex">
        <div className="w-1/3 bg-secondary-100 -input border-r border-gray-200 flex flex-col gap-2 py-2 px-4">
          <div className="font-inter font-medium text-[16px] text-foreground">{label}</div>
        </div>
        <div className="flex-1 bg-background py-2 px-4 flex flex-col gap-2">
          <div className="font-inter font-medium text-[16px] text-foreground">{value}</div>
        </div>
      </div>
    )
  }

  if (!isOpen || !user?.userType) return null

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="flex flex-col items-center gap-3 w-full max-w-[600px] mx-auto px-4">
          <ExclamationTriangleIcon className="text-error-900 w-[64px] h-[64px]" />
          <div>
            <h2 className="text-center font-bold text-[19.2px] leading-[23px] capitalize text-foreground">
              {content[userType]?.modalTitle || 'Suspend User?'}
            </h2>
            <p className="text-center font-normal text-[16px] leading-[19px] text-foreground-muted w-full px-10 mx-auto">
              {content[userType]?.modalDescription || ''}
            </p>
          </div>

          <div className="w-full flex flex-col border border-text-medium-opacity rounded-[4px]">
            {renderDisplayItem('Shop Name', name || '—')}
            {renderDisplayItem('Building', buildingName || '—')}
            {renderDisplayItem('Floor', floor || '—')}
            {renderDisplayItem('Shop Number', shopNumber || '—')}
            {renderDisplayItem('Status', <DisplayActiveSuspended isActive={isActive} />)}
            {renderDisplayItem('Create Date', createdAt ? formatTime(createdAt) : '—')}
          </div>

          <div className="w-full">
            <textarea
              id="suspensionReason"
              placeholder="Suspension reason is ..."
              rows={2}
              value={suspensionReason}
              onChange={(e) => setSuspensionReason(e.target.value)}
              className="w-full px-3 py-2 border border-bg-neutral-200 rounded-md shadow-sm focus:outline-none focus:ring-error-900 focus:border-error-900"
            />
          </div>

          <div className="flex gap-4 flex-wrap justify-center w-3/4">
            <button
              onClick={handleConfirm}
              disabled={isConfirming || !suspensionReason.trim()}
              className={`px-5 py-2 flex-1 flex items-center justify-center shadow-md rounded-[10px] font-medium text-[18px] capitalize text-white bg-gradient-to-r from-error-700 to-error-800 hover:opacity-90 ${
                isConfirming ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isConfirming ? 'Suspending...' : 'Suspend'}
            </button>

            <button
              onClick={onClose}
              disabled={isConfirming}
              className="px-5 py-2 flex-1 flex items-center justify-center border border-error-900 shadow-md rounded-[10px] font-medium text-[18px] capitalize text-error-900 hover:bg-background-secondary disabled:opacity-70 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default SuspendModal
